import { useMemo, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { confirmDialog } from "@/lib/confirm-dialog";
import {
  ArrowRightLeft,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
  Upload,
} from "lucide-react";
import { AdminUploadButton } from "@/components/admin/AdminUploadButton";

import {
  listRedirects,
  upsertRedirect,
  deleteRedirect,
  toggleRedirect,
  type RedirectRow,
} from "@/lib/redirects.functions";

export const Route = createFileRoute("/_admin/admin/redirects")({
  component: RedirectsPage,
});

/**
 * Normalisiert einen Quellpfad: Ganze URL → nur pathname (+search),
 * fehlt der führende Slash, wird er ergänzt.
 */
function normalizeSourcePath(input: string): string {
  const raw = input.trim();
  if (!raw) return raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      return (u.pathname || "/") + (u.search || "");
    } catch {
      // fällt unten durch
    }
  }
  return raw.startsWith("/") ? raw : `/${raw}`;
}

type Draft = {
  id?: string;
  source_path: string;
  destination_url: string;
  status_code: 301 | 302 | 307 | 308;
  enabled: boolean;
  notes: string;
};

const EMPTY_DRAFT: Draft = {
  source_path: "",
  destination_url: "",
  status_code: 301,
  enabled: true,
  notes: "",
};

function RedirectsPage() {
  const listFn = useServerFn(listRedirects);
  const upsertFn = useServerFn(upsertRedirect);
  const deleteFn = useServerFn(deleteRedirect);
  const toggleFn = useServerFn(toggleRedirect);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-redirects"],
    queryFn: () => listFn(),
  });

  const [editing, setEditing] = useState<Draft | null>(null);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [filter, setFilter] = useState("");
  const [csvOpen, setCsvOpen] = useState(false);

  const rows = data?.redirects ?? [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.source_path.toLowerCase().includes(q) ||
        r.destination_url.toLowerCase().includes(q) ||
        (r.notes ?? "").toLowerCase().includes(q),
    );
  }, [rows, filter]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin-redirects"] });

  const saveMutation = useMutation({
    mutationFn: (draft: Draft) =>
      upsertFn({
        data: {
          id: draft.id,
          source_path: normalizeSourcePath(draft.source_path),
          destination_url: draft.destination_url.trim(),
          status_code: draft.status_code,
          enabled: draft.enabled,
          notes: draft.notes.trim() ? draft.notes.trim() : null,
        },
      }),
    onSuccess: () => {
      setEditing(null);
      setFeedback({ kind: "ok", msg: "Weiterleitung gespeichert." });
      invalidate();
    },
    onError: (err: Error) =>
      setFeedback({ kind: "err", msg: err.message ?? "Speichern fehlgeschlagen." }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      setFeedback({ kind: "ok", msg: "Weiterleitung gelöscht." });
      invalidate();
    },
    onError: (err: Error) =>
      setFeedback({ kind: "err", msg: err.message ?? "Löschen fehlgeschlagen." }),
  });

  const toggleMutation = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) => toggleFn({ data: v }),
    onSuccess: () => invalidate(),
    onError: (err: Error) =>
      setFeedback({ kind: "err", msg: err.message ?? "Aktualisieren fehlgeschlagen." }),
  });

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">Weiterleitungen</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Legen Sie 301-Weiterleitungen von alten Pfaden auf neue URLs an. Aktive Regeln
            werden serverseitig ausgeliefert – Änderungen greifen binnen einer Minute.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCsvOpen(true)}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold text-foreground hover:bg-secondary"
          >
            <Upload className="h-4 w-4" /> CSV-Import
          </button>
          <button
            type="button"
            onClick={() => {
              setFeedback(null);
              setEditing({ ...EMPTY_DRAFT });
            }}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-3 py-2 text-sm font-semibold text-brand-button-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> Neue Weiterleitung
          </button>
        </div>
      </div>

      {feedback ? (
        <div
          className={
            "mt-6 rounded-md border px-4 py-3 text-sm " +
            (feedback.kind === "ok"
              ? "border-brand-wahlen/40 bg-brand-wahlen/10 text-primary"
              : "border-brand-accent/40 bg-brand-accent/10 text-brand-accent")
          }
        >
          {feedback.msg}
        </div>
      ) : null}

      <div className="mt-6 flex items-center gap-3">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Suchen (Quelle, Ziel, Notiz)…"
          className="w-full max-w-sm rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
        <span className="text-xs text-muted-foreground">
          {filtered.length} von {rows.length}
        </span>
      </div>

      <div className="mt-4 overflow-hidden rounded-md border border-border">
        {isLoading ? (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Lade Weiterleitungen…
          </div>
        ) : isError ? (
          <div className="px-4 py-6 text-sm text-brand-accent">
            Fehler beim Laden: {(error as Error)?.message}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-start gap-2 px-4 py-8 text-sm text-muted-foreground">
            <ArrowRightLeft className="h-6 w-6 text-muted-foreground/70" />
            Noch keine Weiterleitungen angelegt.
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-semibold">Quelle</th>
                <th className="px-3 py-2 font-semibold">Ziel</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Aktiv</th>
                <th className="px-3 py-2 font-semibold">Aktualisiert</th>
                <th className="px-3 py-2 font-semibold text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="px-3 py-3">
                    <code className="text-xs">{r.source_path}</code>
                    {r.notes ? (
                      <p className="mt-1 text-xs text-muted-foreground">{r.notes}</p>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 break-all">
                    <code className="text-xs">{r.destination_url}</code>
                  </td>
                  <td className="px-3 py-3 text-xs">{r.status_code}</td>
                  <td className="px-3 py-3">
                    <label className="inline-flex cursor-pointer items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={r.enabled}
                        onChange={(e) =>
                          toggleMutation.mutate({ id: r.id, enabled: e.target.checked })
                        }
                      />
                      {r.enabled ? "aktiv" : "aus"}
                    </label>
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">
                    {new Date(r.updated_at).toLocaleString("de-DE")}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setFeedback(null);
                          setEditing({
                            id: r.id,
                            source_path: r.source_path,
                            destination_url: r.destination_url,
                            status_code: r.status_code as Draft["status_code"],
                            enabled: r.enabled,
                            notes: r.notes ?? "",
                          });
                        }}
                        className="rounded p-1.5 hover:bg-secondary"
                        aria-label="Bearbeiten"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            await confirmDialog({
                              title: `Weiterleitung „${r.source_path}" löschen?`,
                              description:
                                "Aufrufe dieser Adresse führen danach auf die 404-Seite.",
                            })
                          ) {
                            deleteMutation.mutate(r.id);
                          }
                        }}
                        className="rounded p-1.5 text-brand-accent hover:bg-brand-accent/10"
                        aria-label="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing ? (
        <EditDialog
          draft={editing}
          onClose={() => setEditing(null)}
          onSubmit={(d) => saveMutation.mutate(d)}
          saving={saveMutation.isPending}
        />
      ) : null}

      {csvOpen ? (
        <CsvImportDialog
          existing={rows}
          onClose={() => setCsvOpen(false)}
          upsert={(draft) => upsertFn({ data: draft })}
          onDone={(msg) => {
            setFeedback({ kind: "ok", msg });
            invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

function EditDialog({
  draft,
  onClose,
  onSubmit,
  saving,
}: {
  draft: Draft;
  onClose: () => void;
  onSubmit: (d: Draft) => void;
  saving: boolean;
}) {
  const [d, setD] = useState<Draft>(draft);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(d);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-md border border-border bg-background p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <h2 className="font-display text-xl font-extrabold">
            {d.id ? "Weiterleitung bearbeiten" : "Neue Weiterleitung"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-secondary"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <label className="block text-sm">
            <span className="mb-1 block font-semibold">Quellpfad</span>
            <input
              value={d.source_path}
              onChange={(e) => setD({ ...d, source_path: e.target.value })}
              placeholder="/alter-pfad"
              className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs outline-none focus:border-primary"
              required
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              Muss mit / beginnen, keine Leerzeichen, kein ? oder #.
            </span>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-semibold">Ziel</span>
            <input
              value={d.destination_url}
              onChange={(e) => setD({ ...d, destination_url: e.target.value })}
              placeholder="/neuer-pfad oder https://…"
              className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs outline-none focus:border-primary"
              required
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              Interner Pfad mit / oder absolute https://-URL.
            </span>
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="block text-sm">
              <span className="mb-1 block font-semibold">Status</span>
              <select
                value={d.status_code}
                onChange={(e) =>
                  setD({ ...d, status_code: Number(e.target.value) as Draft["status_code"] })
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              >
                <option value={301}>301 – dauerhaft</option>
                <option value={302}>302 – temporär</option>
                <option value={307}>307 – temporär (Methode)</option>
                <option value={308}>308 – dauerhaft (Methode)</option>
              </select>
            </label>
            <label className="mt-6 inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={d.enabled}
                onChange={(e) => setD({ ...d, enabled: e.target.checked })}
              />
              Aktiv
            </label>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-semibold">Notiz (optional)</span>
            <textarea
              value={d.notes}
              onChange={(e) => setD({ ...d, notes: e.target.value })}
              rows={2}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground hover:opacity-90 disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

type CsvResult = { line: number; source: string; status: "ok" | "skip" | "err"; msg?: string };

type FieldKey = "source_path" | "destination_url" | "status_code" | "enabled" | "notes";

type Mapping = Record<FieldKey, number>; // -1 = nicht zugeordnet

const FIELD_LABELS: Record<FieldKey, string> = {
  source_path: "Quellpfad *",
  destination_url: "Ziel-URL *",
  status_code: "Status-Code",
  enabled: "Aktiv",
  notes: "Notiz",
};

const HEADER_HINTS: Record<FieldKey, string[]> = {
  source_path: ["source", "source_path", "from", "quelle", "quellpfad", "old", "old url", "old_url", "alt", "path"],
  destination_url: ["destination", "destination_url", "to", "ziel", "ziel-url", "target", "new", "new url", "new_url", "url"],
  status_code: ["status", "status_code", "code", "http", "http_status"],
  enabled: ["enabled", "active", "aktiv", "on"],
  notes: ["notes", "note", "notiz", "kommentar", "comment"],
};

function stripBom(s: string) {
  return s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
}

function detectDelimiter(firstLine: string): string {
  const candidates = [",", ";", "\t", "|"];
  let best = ",";
  let bestCount = -1;
  for (const c of candidates) {
    const n = firstLine.split(c).length;
    if (n > bestCount) {
      bestCount = n;
      best = c;
    }
  }
  return best;
}

function parseCsv(input: string, delimiter: string): string[][] {
  const text = stripBom(input);
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === delimiter) {
        row.push(field);
        field = "";
      } else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && text[i + 1] === "\n") i++;
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      } else {
        field += ch;
      }
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim().length > 0));
}

function autoMap(headers: string[]): Mapping {
  const norm = headers.map((h) => h.trim().toLowerCase());
  const map: Mapping = {
    source_path: -1,
    destination_url: -1,
    status_code: -1,
    enabled: -1,
    notes: -1,
  };
  (Object.keys(HEADER_HINTS) as FieldKey[]).forEach((field) => {
    const hints = HEADER_HINTS[field];
    const idx = norm.findIndex((h) => hints.includes(h));
    if (idx >= 0) map[field] = idx;
  });
  return map;
}

function parseEnabled(v: string | undefined): boolean {
  if (v == null) return true;
  const s = v.trim().toLowerCase();
  if (!s) return true;
  return ["1", "true", "wahr", "ja", "yes", "y", "on", "aktiv", "active"].includes(s);
}

function CsvImportDialog({
  existing,
  onClose,
  upsert,
  onDone,
}: {
  existing: RedirectRow[];
  onClose: () => void;
  upsert: (draft: {
    source_path: string;
    destination_url: string;
    status_code: 301 | 302 | 307 | 308;
    enabled: boolean;
    notes: string | null;
  }) => Promise<unknown>;
  onDone: (msg: string) => void;
}) {
  const [step, setStep] = useState<"upload" | "map" | "run">("upload");
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [delimiter, setDelimiter] = useState<"auto" | "," | ";" | "\t" | "|">("auto");
  const [hasHeader, setHasHeader] = useState(true);
  const [rows, setRows] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Mapping>({
    source_path: -1,
    destination_url: -1,
    status_code: -1,
    enabled: -1,
    notes: -1,
  });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<CsvResult[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const existingSources = useMemo(
    () => new Set(existing.map((r) => r.source_path)),
    [existing],
  );

  const readFile = async (file: File) => {
    setFileName(file.name);
    const t = await file.text();
    setText(t);
  };

  const goToMapping = () => {
    setParseError(null);
    if (!text.trim()) {
      setParseError("Bitte Datei wählen oder Text einfügen.");
      return;
    }
    const firstLine = stripBom(text).split(/\r?\n/, 1)[0] ?? "";
    const delim = delimiter === "auto" ? detectDelimiter(firstLine) : delimiter;
    const parsed = parseCsv(text, delim);
    if (parsed.length === 0) {
      setParseError("Keine Daten gefunden.");
      return;
    }
    let hdrs: string[];
    let dataRows: string[][];
    if (hasHeader) {
      hdrs = parsed[0].map((h) => h.trim());
      dataRows = parsed.slice(1);
    } else {
      const cols = Math.max(...parsed.map((r) => r.length));
      hdrs = Array.from({ length: cols }, (_, i) => `Spalte ${i + 1}`);
      dataRows = parsed;
    }
    setHeaders(hdrs);
    setRows(dataRows);
    setMapping(hasHeader ? autoMap(hdrs) : { source_path: 0, destination_url: 1, status_code: 2, enabled: -1, notes: -1 });
    setStep("map");
  };

  const runImport = async () => {
    if (mapping.source_path < 0 || mapping.destination_url < 0) return;
    setBusy(true);
    setStep("run");
    setResults(null);
    setProgress({ done: 0, total: rows.length });
    const out: CsvResult[] = [];
    const seenInBatch = new Set<string>();
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const lineNo = hasHeader ? i + 2 : i + 1;
      const source = (row[mapping.source_path] ?? "").trim();
      const destination = (row[mapping.destination_url] ?? "").trim();
      const statusRaw =
        mapping.status_code >= 0 ? (row[mapping.status_code] ?? "").trim() : "";
      const enabledRaw =
        mapping.enabled >= 0 ? (row[mapping.enabled] ?? "").trim() : "";
      const notesRaw = mapping.notes >= 0 ? (row[mapping.notes] ?? "").trim() : "";

      if (!source || !destination) {
        out.push({ line: lineNo, source: source || "(leer)", status: "err", msg: "Quelle oder Ziel fehlt" });
        setProgress({ done: i + 1, total: rows.length });
        continue;
      }
      if (source === destination) {
        out.push({ line: lineNo, source, status: "err", msg: "Quelle und Ziel dürfen nicht identisch sein" });
        setProgress({ done: i + 1, total: rows.length });
        continue;
      }
      const normalizedSource = normalizeSourcePath(source);
      if (!normalizedSource.startsWith("/")) {
        out.push({ line: lineNo, source, status: "err", msg: "Quellpfad muss mit / beginnen" });
        setProgress({ done: i + 1, total: rows.length });
        continue;
      }
      if (!(destination.startsWith("/") || /^https?:\/\//.test(destination))) {
        out.push({ line: lineNo, source, status: "err", msg: "Ziel muss mit / beginnen oder http(s):// sein" });
        setProgress({ done: i + 1, total: rows.length });
        continue;
      }
      if (existingSources.has(normalizedSource) || seenInBatch.has(normalizedSource)) {
        out.push({ line: lineNo, source: normalizedSource, status: "skip", msg: "existiert bereits" });
        setProgress({ done: i + 1, total: rows.length });
        continue;
      }
      const status = statusRaw ? Number(statusRaw) : 301;
      if (![301, 302, 307, 308].includes(status)) {
        out.push({ line: lineNo, source: normalizedSource, status: "err", msg: `Status ${statusRaw} ungültig` });
        setProgress({ done: i + 1, total: rows.length });
        continue;
      }
      try {
        await upsert({
          source_path: normalizedSource,
          destination_url: destination,
          status_code: status as 301 | 302 | 307 | 308,
          enabled: mapping.enabled >= 0 ? parseEnabled(enabledRaw) : true,
          notes: notesRaw ? notesRaw.slice(0, 500) : null,
        });
        seenInBatch.add(normalizedSource);
        out.push({ line: lineNo, source: normalizedSource, status: "ok" });
      } catch (err) {
        out.push({
          line: lineNo,
          source,
          status: "err",
          msg: (err as Error).message ?? "Fehler",
        });
      }
      setProgress({ done: i + 1, total: rows.length });
    }
    setResults(out);
    setBusy(false);
    const ok = out.filter((r) => r.status === "ok").length;
    if (ok > 0) onDone(`${ok} Weiterleitung(en) importiert.`);
  };

  const downloadErrors = () => {
    if (!results) return;
    const errs = results.filter((r) => r.status === "err");
    if (errs.length === 0) return;
    const csv =
      "line,source,message\n" +
      errs
        .map((r) => `${r.line},"${r.source.replace(/"/g, '""')}","${(r.msg ?? "").replace(/"/g, '""')}"`)
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "redirect-import-errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const previewRows = rows.slice(0, 5);
  const canImport = mapping.source_path >= 0 && mapping.destination_url >= 0 && rows.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-md border border-border bg-background p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-xl font-extrabold">CSV-Import</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Schritt {step === "upload" ? 1 : step === "map" ? 2 : 3} von 3 ·{" "}
              {step === "upload" ? "Datei wählen" : step === "map" ? "Spalten zuordnen" : "Import"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-secondary"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === "upload" ? (
          <div className="mt-5 space-y-4">
            <div>
              <span className="mb-1 block text-sm font-semibold">CSV-Datei</span>
              <AdminUploadButton
                label={fileName ? "Andere CSV-Datei wählen" : "CSV-Datei auswählen"}
                accept=".csv,text/csv,text/plain"
                currentName={fileName || null}
                hint={fileName ? undefined : "Spalten: Quelle und Ziel, optional Art der Weiterleitung."}
                onFiles={(files) => {
                  const f = files[0];
                  if (f) readFile(f);
                }}
              />
            </div>


            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1 block font-semibold">Trennzeichen</span>
                <select
                  value={delimiter}
                  onChange={(e) => setDelimiter(e.target.value as typeof delimiter)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                >
                  <option value="auto">Automatisch erkennen</option>
                  <option value=",">Komma (,)</option>
                  <option value=";">Semikolon (;)</option>
                  <option value={"\t"}>Tab</option>
                  <option value="|">Pipe (|)</option>
                </select>
              </label>
              <label className="mt-6 inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={hasHeader}
                  onChange={(e) => setHasHeader(e.target.checked)}
                />
                Erste Zeile ist Kopfzeile
              </label>
            </div>

            <details className="rounded-md border border-border bg-secondary/40 p-3 text-xs">
              <summary className="cursor-pointer font-semibold">
                Alternativ: Text einfügen
              </summary>
              <textarea
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setFileName(null);
                }}
                rows={6}
                placeholder="source,destination,status&#10;/altes-impressum,/impressum,301"
                className="mt-2 w-full rounded-md border border-border bg-background p-2 font-mono text-xs outline-none focus:border-primary"
              />
            </details>

            {parseError ? (
              <div className="rounded-md border border-brand-accent/40 bg-brand-accent/10 px-3 py-2 text-xs text-brand-accent">
                {parseError}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={goToMapping}
                disabled={!text.trim()}
                className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground hover:opacity-90 disabled:opacity-60"
              >
                Weiter zur Zuordnung
              </button>
            </div>
          </div>
        ) : null}

        {step === "map" ? (
          <div className="mt-5 space-y-4">
            <p className="text-xs text-muted-foreground">
              {rows.length} Zeile{rows.length === 1 ? "" : "n"} erkannt. Ordnen Sie die
              Spalten den Zielfeldern zu. Pflichtfelder sind mit * markiert.
            </p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {(Object.keys(FIELD_LABELS) as FieldKey[]).map((field) => (
                <label key={field} className="block text-sm">
                  <span className="mb-1 block font-semibold">{FIELD_LABELS[field]}</span>
                  <select
                    value={mapping[field]}
                    onChange={(e) =>
                      setMapping({ ...mapping, [field]: Number(e.target.value) })
                    }
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                  >
                    <option value={-1}>— nicht zuordnen —</option>
                    {headers.map((h, i) => (
                      <option key={i} value={i}>
                        {h || `Spalte ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

            <div className="overflow-auto rounded-md border border-border">
              <table className="w-full text-left text-xs">
                <thead className="bg-secondary/60 uppercase tracking-wider text-muted-foreground">
                  <tr>
                    {headers.map((h, i) => (
                      <th key={i} className="px-2 py-1 font-semibold">
                        {h || `Spalte ${i + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r, ri) => (
                    <tr key={ri} className="border-t border-border">
                      {headers.map((_, ci) => (
                        <td key={ci} className="px-2 py-1 font-mono">
                          {r[ci] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => setStep("upload")}
                className="rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"
              >
                Zurück
              </button>
              <button
                type="button"
                onClick={runImport}
                disabled={!canImport}
                className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground hover:opacity-90 disabled:opacity-60"
              >
                Import starten ({rows.length})
              </button>
            </div>
          </div>
        ) : null}

        {step === "run" ? (
          <div className="mt-5 space-y-4">
            {busy && progress ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Importiere {progress.done} von {progress.total}…
              </div>
            ) : null}

            {results ? (
              <>
                <div className="flex flex-wrap gap-3 text-xs">
                  <span className="rounded-md bg-brand-wahlen/10 px-2 py-1 text-primary">
                    OK: {results.filter((r) => r.status === "ok").length}
                  </span>
                  <span className="rounded-md bg-secondary px-2 py-1 text-muted-foreground">
                    Übersprungen: {results.filter((r) => r.status === "skip").length}
                  </span>
                  <span className="rounded-md bg-brand-accent/10 px-2 py-1 text-brand-accent">
                    Fehler: {results.filter((r) => r.status === "err").length}
                  </span>
                </div>

                <div className="max-h-64 overflow-auto rounded-md border border-border text-xs">
                  <table className="w-full">
                    <tbody>
                      {results.map((r) => (
                        <tr key={r.line} className="border-t border-border first:border-t-0">
                          <td className="px-2 py-1 text-muted-foreground">Z. {r.line}</td>
                          <td className="px-2 py-1 font-mono">{r.source}</td>
                          <td className="px-2 py-1">
                            {r.status === "ok" ? (
                              <span className="text-brand-wahlen">OK</span>
                            ) : r.status === "skip" ? (
                              <span className="text-muted-foreground">übersprungen</span>
                            ) : (
                              <span className="text-brand-accent">Fehler</span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">{r.msg ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            <div className="flex justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setStep("map");
                  setResults(null);
                  setProgress(null);
                }}
                disabled={busy}
                className="rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary disabled:opacity-60"
              >
                Zurück
              </button>
              <div className="flex gap-2">
                {results && results.some((r) => r.status === "err") ? (
                  <button
                    type="button"
                    onClick={downloadErrors}
                    className="rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"
                  >
                    Fehler als CSV
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground hover:opacity-90 disabled:opacity-60"
                >
                  Fertig
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
